<template>
  <section v-if="pageContent" class="choose-appointment">
    <div class="choose-appointment__wrapper">
      <p class="choose-appointment__title">
        {{ pageContent.stepTitle }}
      </p>
      <p
        v-if="!isRepairAppointment && appointmentInstallText"
        data-testid="appointment-install-info"
        class="choose-appointment__install-info"
      >
        {{ appointmentInstallText }}
      </p>
      <Article
        v-if="isRepairAppointment && appointmentTypeCopy"
        :text-content="appointmentTypeCopy"
        class="choose-appointment__repair-info"
        no-margin
      />
      <Spinner v-if="slotsPending" spinner-text="Loading..." />
      <div v-else>
        <p class="choose-appointment__address">
          {{ pageContent.installationAddressText }} {{ fullAddress }}
        </p>

        <p class="choose-appointment__select-time-text">
          {{ pageContent.selectDateText }}
        </p>
        <div class="choose-appointment__slots">
          <RadioButton
            v-for="(slot, index) in flattenedSlots"
            :key="`slot-${index}`"
            v-model="selectedAppointmentSlot"
            :value="slot"
            :input-id="`slot-${index}`"
            name="appointment-slot"
          >
            <div class="appointment-option">
              <div class="appointment-option__title">
                {{ formatSlotDate(slot) }}
              </div>
              <div class="appointment-option__subtitle">
                {{ formatSlotTime(slot) }}
              </div>
            </div>
          </RadioButton>
          <RadioButton
            v-model="selectedAppointmentSlot"
            :value="{ isRegisterInterest: true }"
            input-id="register-interest"
            name="appointment-slot"
          >
            <div class="appointment-option">
              <div class="appointment-option__title">Other times</div>
              <div class="appointment-option__subtitle">
                Let us know when's best for you
              </div>
            </div>
          </RadioButton>
        </div>
        <FeedbackDialog
          v-if="appointmentInfoContent && selectedAppointmentSlot?.date"
          class="choose-appointment__info-dialog"
          variant="info"
          full-width
        >
          <Article :text-content="appointmentInfoContent" no-margin />
        </FeedbackDialog>
        <AppButton
          v-if="nextButtonProps"
          v-track="nextButtonProps.tracking"
          class="choose-appointment__next-button"
          :disabled="!validated"
          @click="handleContinueClick"
        >
          <span>{{ nextButtonProps.label }}</span>
          <span class="choose-appointment__icon icon icon-arrow-right" />
        </AppButton>
      </div>
    </div>
  </section>
  <EligibilityModal
    v-if="modal.isModalVisible"
    :show="modal.isModalVisible"
    :variation="modal.variation"
  />
</template>

<script>
import { mapGetters, mapState } from "vuex"
import GatewayPortal from "@/services/GatewayPortal"
import AppButton from "@soenergy/frontend-library/src/components/AppButton.vue"
import Spinner from "@soenergy/frontend-library/src/components/Spinner.vue"
import Article from "@soenergy/frontend-library/src/components/Article.vue"
import RadioButton from "@soenergy/frontend-library/src/components/RadioButton/RadioButton.vue"
import FeedbackDialog from "@soenergy/frontend-library/src/components/FeedbackDialog.vue"
import cmsPreviewMixin from "@soenergy/frontend-library/src/mixins/cmsPreviewMixin"
import EligibilityModal from "@/components/EligibilityModal.vue"
import chooseAppointmentContent from "soenergy-cms-loader!?path=smbp/choose-appointment"
import TrackingService from "@soenergy/frontend-library/src/services/TrackingService"
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"
import {
  formatLongDate,
  formatTimeRange,
} from "@soenergy/frontend-library/src/helpers/dateFormat"

export default {
  components: {
    AppButton,
    EligibilityModal,
    RadioButton,
    Spinner,
    Article,
    FeedbackDialog,
  },
  mixins: [cmsPreviewMixin({ story: chooseAppointmentContent })],
  props: {
    currentStepNumber: {
      type: Number,
      default: null,
    },
  },
  emits: ["go-forward"],
  data() {
    return {
      availableSlots: [],
      days: [],
      slotsPending: false,
      modal: {
        isModalVisible: false,
        variation: null,
      },
    }
  },
  computed: {
    ...mapState({
      meterRepairEligibility: (state) => state.meterRepairEligibility,
      meterInstallEligibility: (state) => state.meterInstallEligibility,
      customerId: (state) => state.customerId,
    }),
    ...mapGetters([
      "fullAddress",
      "validatedPostcode",
      "hasActiveAgreements",
      "isSwitchingAway",
      "hasSmartMeter",
      "isEligibleForInstall",
      "isEligibleForRepair",
      "mopCode",
      "ineligibleTooComplex",
      "meterTypeKey",
      "mpan",
      "mprn",
    ]),
    selectedAppointmentSlot: {
      get() {
        return this.$store.state.appointment.selectedAppointmentSlot
      },
      set(value) {
        this.$store.commit("SET_SELECTED_APPOINTMENT_SLOT", value)
      },
    },
    flattenedSlots() {
      const slots = []
      this.availableSlots.forEach((day) => {
        if (day.slots && day.slots.length > 0) {
          day.slots.forEach((timeSlot) => {
            slots.push({
              date: day.date,
              time: timeSlot,
            })
          })
        }
      })
      return slots
    },
    validated() {
      return (
        ((!!this.selectedAppointmentSlot?.date &&
          !!this.selectedAppointmentSlot?.time) ||
          !!this.selectedAppointmentSlot?.isRegisterInterest) &&
        !this.slotsPending
      )
    },
    nextButtonProps() {
      return this.pageContent?.nextStepButton?.[0] ?? null
    },
    appointmentInfoContent() {
      return this.pageContent?.appointmentInfoMessage?.[0]?.textContent ?? null
    },
    isRepairAppointment() {
      return this.$route.meta.appointmentType === "repair"
    },
    appointmentTypeCopy() {
      const meterRepairEligibility =
        this.meterRepairEligibility?.appointmentType
      const appointmentTypeCopies = this.pageContent?.appointmentTypeCopies
      if (!meterRepairEligibility) return null

      const appointmentCopy = appointmentTypeCopies
        .flatMap((copy) => copy.appointmentTypeCopy)
        .find((copy) => copy.name === meterRepairEligibility)

      return appointmentCopy?.text?.content?.[0] ?? null
    },
    appointmentInstallText() {
      const upgradeGas =
        "We'll upgrade your gas meter to the latest generation of smart meter."
      const upgradeElec =
        "We'll upgrade your electricity meter to the latest generation of smart meter."
      const replaceGas = "We'll replace your offline gas smart meter."
      const replaceElec = "We'll replace your offline electricity smart meter."
      const upgradeBoth =
        "We'll upgrade both your gas and electricity meters to the latest generation of smart meters."

      const texts = {
        "DUAL_TRAD-TRAD":
          "We'll upgrade both your traditional gas and electricity meters to the latest generation of smart meters.",
        "DUAL_SMETS1-TRAD": upgradeBoth,
        "DUAL_SMETS1-SMETS1": upgradeBoth,
        "DUAL_SMETS1-SMETS2":
          "We'll replace your offline smart meter and upgrade your other smart meter to the latest generation.",
        "DUAL_SMETS2-TRAD":
          "We'll replace your offline smart meter and upgrade your traditional meter to the latest generation.",
        "DUAL_SMETS2-SMETS2": "We'll replace both your offline smart meters.",
        DUAL_GAS_ONLY: upgradeGas,
        DUAL_GAS_ONLY_SMETS2: replaceGas,
        DUAL_ELEC_ONLY: upgradeElec,
        DUAL_ELEC_ONLY_SMETS2: replaceElec,
        SINGLE_GAS: upgradeGas,
        SINGLE_GAS_SMETS2: replaceGas,
        SINGLE_ELEC: upgradeElec,
        SINGLE_ELEC_SMETS2: replaceElec,
      }
      return texts[this.meterTypeKey] ?? null
    },
  },
  async mounted() {
    this.slotsPending = true
    try {
      await this.$store.dispatch("bookingDetails/fetchBookingData")
      if (this.$store.getters["bookingDetails/hasBookings"]) {
        this.$router.push({ path: "/appointment-details" })
        return
      }
    } catch (e) {
      ErrorReporter.report(e)
    }
    this.checkEligibility()
  },
  methods: {
    formatSlotDate(slot) {
      return formatLongDate(slot.date)
    },
    formatSlotTime(slot) {
      if (!slot.time) return ""
      return formatTimeRange(slot.time.startTime, slot.time.endTime)
    },
    handleContinueClick() {
      const isRegisterInterest =
        !!this.selectedAppointmentSlot?.isRegisterInterest
      this.nextStep(isRegisterInterest)
    },
    nextStep(isRegisterInterestFlow) {
      this.$store.commit("SET_REGISTER_INTEREST_FLOW", isRegisterInterestFlow)
      this.$nextTick(() => this.$emit("go-forward"))
    },
    fetchSlots() {
      GatewayPortal.getAvailableSlots(
        this.validatedPostcode,
        this.customerId,
        this.mopCode,
        this.mpan,
        this.mprn
      )
        .then((response) => {
          if (response && response.data && response.data.days) {
            TrackingService.sendEvent("success-fetch-slots-choose-appointment")
            this.availableSlots = response.data.days
            this.$store.commit(
              "SET_IS_AVAILABLE_SLOTS",
              this.availableSlots.some((slot) => slot.slots.length > 0)
            )
            if (this.$store.state.isAvailableSlots) {
              this.days = response.data.days
                .filter((day) => day.slots.length)
                .map((day) => new Date(day.date))
              this.$store.commit("RESET_CALENDER")
              return
            }
            this.nextStep(true)
          }
        })
        .catch((err) => {
          this.$store.commit("RESET_CALENDER")
          throw err
        })
        .finally(() => {
          if (this.days.length) {
            TrackingService.sendEvent("has-available-slots-choose-appointment")
          }
          this.slotsPending = false
        })
    },
    checkEligibility() {
      if (this.isRepairAppointment) {
        this.handleRepairEligibility()
      } else {
        this.handleInstallEligibility()
      }
    },
    handleRepairEligibility() {
      if (!this.isEligibleForRepair) {
        return this.setModal("repairIneligible")
      }
      return this.fetchSlots()
    },
    handleInstallEligibility() {
      switch (true) {
        case this.isSwitchingAway:
          return this.setModal("switchingAway")
        case !this.hasActiveAgreements:
          return this.setModal("notOnSupply")
        case this.hasSmartMeter:
          return this.setModal("hasSmartMeter")
        case this.ineligibleTooComplex:
          return this.setModal("ineligibleTooComplex")
        case !this.isEligibleForInstall:
          return this.setModal("ineligible")
        default:
          return this.fetchSlots()
      }
    },
    setModal(modalVariant) {
      this.modal.variation = modalVariant
      this.modal.isModalVisible = true
      this.$store.commit("RESET_CALENDER")
    },
  },
}
</script>

<style scoped lang="scss">
.choose-appointment {
  &__wrapper {
    margin-bottom: $space-16;

    @include md {
      margin-bottom: $space-12;
    }
  }

  &__icon {
    margin-left: $space-1;
  }

  &__title {
    font-size: $font-size-5;
    font-weight: $weight-medium;
    margin-bottom: $space-4;

    @include md {
      font-size: $font-size-6;
    }
  }

  &__address {
    font-size: $font-size-3;
    font-weight: $weight-medium;

    @include md {
      font-size: $font-size-5;
    }
  }

  &__select-time-text {
    font-size: $font-size-3;
    margin-top: $space-5;
    margin-bottom: $space-5;

    @include md {
      font-size: $font-size-4;
      margin-top: $space-6;
      margin-bottom: $space-7;
    }
  }

  &__slots {
    margin-bottom: $space-4;
  }

  &__info-dialog {
    margin-top: $space-7;
    margin-bottom: $space-4;

    @include md {
      margin-top: $space-9;
      margin-bottom: $space-4;
    }
  }

  &__next-button {
    width: 100%;
    margin-top: $space-3;

    @include md {
      margin-top: $space-5;
    }
  }

  &__install-info {
    font-size: $font-size-3;
    margin-bottom: $space-5;

    @include md {
      font-size: $font-size-5;
    }
  }

  &__repair-info {
    font-size: $font-size-3;
    margin-bottom: $space-5;

    @include md {
      font-size: $font-size-4;
    }
  }
}

.appointment-option {
  display: flex;
  flex-direction: column;
  gap: $space-1;

  &__title {
    font-size: $font-size-3;
    margin-bottom: $space-1;

    @include md {
      font-size: $font-size-4;
    }
  }

  &__subtitle {
    font-size: $font-size-2;

    @include md {
      font-size: $font-size-3;
    }
  }
}
</style>
